import { useEffect, useRef, useCallback } from 'react';
import type { ConversationSummary } from '@/api/types';
import { ConversationListItem } from './ConversationListItem';
import { EmptyInbox } from './EmptyInbox';
import { Spinner } from '@/components/ui/Spinner';
import { Button } from '@/components/ui/Button';

interface ConversationListProps {
  conversations: ConversationSummary[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  onRetry: () => void;
  hasFilters: boolean;
  onClearFilters: () => void;
}

export function ConversationList({
  conversations,
  isLoading,
  isError,
  error,
  hasMore,
  isLoadingMore,
  onLoadMore,
  onRetry,
  hasFilters,
  onClearFilters,
}: ConversationListProps) {
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Infinite scroll observer
  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const target = entries[0];
      if (target?.isIntersecting && hasMore && !isLoadingMore) {
        onLoadMore();
      }
    },
    [hasMore, isLoadingMore, onLoadMore]
  );

  useEffect(() => {
    const element = loadMoreRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(handleObserver, {
      threshold: 0.1,
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, [handleObserver]);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner size="lg" />
      </div>
    );
  }

  // Error state
  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4">
        <div className="w-16 h-16 mb-4 bg-red-100 rounded-full flex items-center justify-center">
          <svg
            className="w-8 h-8 text-red-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-1">Failed to load conversations</h3>
        <p className="text-gray-500 text-sm text-center max-w-sm mb-4">
          {error?.message || 'An unexpected error occurred. Please try again.'}
        </p>
        <Button onClick={onRetry}>Try Again</Button>
      </div>
    );
  }

  // Empty state
  if (conversations.length === 0) {
    return <EmptyInbox hasFilters={hasFilters} onClearFilters={onClearFilters} />;
  }

  // List
  return (
    <div className="overflow-y-auto h-full">
      {conversations.map((conversation) => (
        <ConversationListItem key={conversation.id} conversation={conversation} />
      ))}

      {/* Load more trigger */}
      <div ref={loadMoreRef} className="py-4 flex justify-center">
        {isLoadingMore && <Spinner size="md" />}
        {!hasMore && conversations.length > 0 && (
          <p className="text-sm text-gray-400">No more conversations</p>
        )}
      </div>
    </div>
  );
}
